/*
  Illumaesthetic overhaul behaviour. No dependencies; every feature is a
  progressive enhancement and the page works without this file.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- Scroll reveal ---------- */
  function initReveal(root) {
    var items = (root || document).querySelectorAll('.ia-reveal:not(.is-visible)');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Horizontal rails ---------- */
  function initRails(root) {
    (root || document).querySelectorAll('[data-ia-rail]').forEach(function (rail) {
      if (rail.dataset.iaReady) return;
      rail.dataset.iaReady = '1';
      var track = rail.querySelector('.ia-rail__track');
      var prev = rail.querySelector('[data-ia-rail-prev]');
      var next = rail.querySelector('[data-ia-rail-next]');
      if (!track || !prev || !next) return;

      function step() { return Math.max(track.clientWidth * 0.8, 240); }
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        rail.toggleAttribute('data-ia-static', max <= 0);
      }
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- YouTube facade: the iframe only loads when asked for ---------- */
  function initVideo(root) {
    (root || document).querySelectorAll('[data-ia-video]').forEach(function (btn) {
      if (btn.dataset.iaReady) return;
      btn.dataset.iaReady = '1';
      btn.addEventListener('click', function () {
        var id = btn.getAttribute('data-ia-video');
        var frame = document.createElement('iframe');
        frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
        frame.title = btn.getAttribute('data-title') || 'YouTube video';
        frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        frame.allowFullscreen = true;
        btn.parentNode.replaceChild(frame, btn);
        frame.focus();
      });
    });
  }

  /* ---------- Hero video: never autoplay for reduced-motion users ---------- */
  function initHeroVideo() {
    document.querySelectorAll('[data-ia-hero-video]').forEach(function (video) {
      if (reduceMotion) { video.removeAttribute('autoplay'); video.pause(); return; }
      var p = video.play && video.play();
      if (p && p.catch) p.catch(function () {});
    });
  }

  /* ---------- Mobile sticky add to cart ---------- */
  function initStickyAtc() {
    var bar = document.querySelector('[data-ia-sticky-atc]');
    if (!bar) return;
    var form = document.querySelector('.product-single__form, form[action*="/cart/add"]');
    var main = form && form.querySelector('[type="submit"], .add-to-cart');
    if (!main) return;
    var button = bar.querySelector('button');
    var price = bar.querySelector('[data-ia-sticky-price]');
    var mainPrice = document.querySelector('[data-product-price]');

    button.addEventListener('click', function () {
      if (main.disabled) {
        form.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      main.click();
    });

    function sync() {
      button.disabled = false;
      button.textContent = main.disabled ? (button.dataset.soldOut || main.textContent.trim()) : (button.dataset.label || 'Add to cart');
      if (price && mainPrice) price.textContent = mainPrice.textContent.trim();
    }
    new MutationObserver(sync).observe(main, { attributes: true, childList: true, subtree: true });
    sync();

    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      var off = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', off);
      bar.setAttribute('aria-hidden', off ? 'false' : 'true');
    }).observe(main);
  }

  /* ---------- 3D scan preview ----------
     Loads <model-viewer> only after the shopper asks for the preview, and
     only ever with the preview mesh URL the section was given. The purchased
     download is never referenced by the storefront. */
  var MV_SOURCES = [
    'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js',
    'https://unpkg.com/@google/model-viewer@3.5.0/dist/model-viewer.min.js'
  ];
  var mvPromise = null;

  function loadModelViewer() {
    if (window.customElements && customElements.get('model-viewer')) return Promise.resolve();
    if (mvPromise) return mvPromise;
    mvPromise = new Promise(function (resolve, reject) {
      var i = 0;
      (function tryNext() {
        if (i >= MV_SOURCES.length) { mvPromise = null; reject(new Error('model-viewer failed to load')); return; }
        var s = document.createElement('script');
        s.type = 'module';
        s.src = MV_SOURCES[i++];
        s.onload = function () { customElements.whenDefined('model-viewer').then(resolve); };
        s.onerror = function () { s.remove(); tryNext(); };
        document.head.appendChild(s);
      })();
    });
    return mvPromise;
  }

  function formatDims(size, unit) {
    // glTF is metres by definition.
    if (unit === 'in') {
      return [size.x, size.z, size.y].map(function (v) { return (v * 39.3701).toFixed(1); }).join(' × ') + ' in';
    }
    return [size.x, size.z, size.y].map(function (v) { return Math.round(v * 1000); }).join(' × ') + ' mm';
  }

  function initScan(root) {
    (root || document).querySelectorAll('[data-ia-scan]').forEach(function (wrap) {
      if (wrap.dataset.iaReady) return;
      wrap.dataset.iaReady = '1';

      var stage = wrap.querySelector('.ia-scan__stage');
      var poster = wrap.querySelector('.ia-scan__poster');
      var status = wrap.querySelector('.ia-scan__status');
      var tools = wrap.querySelectorAll('[data-ia-scan-tool]');
      var dims = wrap.querySelector('.ia-scan__dims');
      var viewer = null;
      var originals = null;

      function setStatus(text) { if (status) status.textContent = text || ''; }
      function setTools(enabled) { tools.forEach(function (t) { t.disabled = !enabled; }); }
      setTools(false);

      function start() {
        if (viewer) return;
        poster.disabled = true;
        setStatus(wrap.dataset.loadingText || 'Loading preview');
        loadModelViewer().then(function () {
          viewer = document.createElement('model-viewer');
          viewer.setAttribute('src', wrap.dataset.src);
          viewer.setAttribute('alt', wrap.dataset.alt || '3D preview');
          viewer.setAttribute('camera-controls', '');
          viewer.setAttribute('touch-action', 'pan-y');
          viewer.setAttribute('shadow-intensity', '0.8');
          viewer.setAttribute('exposure', '1');
          viewer.setAttribute('environment-image', 'neutral');
          viewer.setAttribute('interaction-prompt', 'auto');
          if (!reduceMotion && wrap.dataset.autorotate === 'true') viewer.setAttribute('auto-rotate', '');
          if (wrap.dataset.ar === 'true') {
            viewer.setAttribute('ar', '');
            viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
          }
          viewer.addEventListener('load', function () {
            poster.hidden = true;
            setStatus('');
            setTools(true);
            var arBtn = wrap.querySelector('[data-ia-scan-tool="ar"]');
            if (arBtn) arBtn.hidden = !viewer.canActivateAR;
            if (dims && viewer.getDimensions) dims.textContent = formatDims(viewer.getDimensions(), wrap.dataset.units);
            var rot = wrap.querySelector('[data-ia-scan-tool="rotate"]');
            if (rot) rot.setAttribute('aria-pressed', viewer.hasAttribute('auto-rotate') ? 'true' : 'false');
          });
          viewer.addEventListener('error', function () {
            setStatus(wrap.dataset.errorText || 'The preview could not be loaded.');
            poster.disabled = false;
          });
          viewer.addEventListener('progress', function (e) {
            var p = e.detail && e.detail.totalProgress;
            if (p && p < 1) setStatus((wrap.dataset.loadingText || 'Loading preview') + ' ' + Math.round(p * 100) + '%');
          });
          stage.insertBefore(viewer, poster);
        }).catch(function () {
          setStatus(wrap.dataset.errorText || 'The preview could not be loaded.');
          poster.disabled = false;
        });
      }

      function setClay(on) {
        if (!viewer || !viewer.model) return;
        var mats = viewer.model.materials;
        if (!originals) {
          originals = mats.map(function (m) {
            var pbr = m.pbrMetallicRoughness;
            return {
              color: pbr.baseColorFactor.slice(),
              texture: pbr.baseColorTexture && pbr.baseColorTexture.texture,
              metal: pbr.metallicFactor,
              rough: pbr.roughnessFactor
            };
          });
        }
        mats.forEach(function (m, i) {
          var pbr = m.pbrMetallicRoughness;
          var o = originals[i];
          if (on) {
            if (pbr.baseColorTexture) pbr.baseColorTexture.setTexture(null);
            pbr.setBaseColorFactor([0.72, 0.74, 0.72, 1]);
            pbr.setMetallicFactor(0);
            pbr.setRoughnessFactor(0.65);
          } else {
            if (pbr.baseColorTexture && o.texture) pbr.baseColorTexture.setTexture(o.texture);
            pbr.setBaseColorFactor(o.color);
            pbr.setMetallicFactor(o.metal);
            pbr.setRoughnessFactor(o.rough);
          }
        });
      }

      poster.addEventListener('click', start);

      tools.forEach(function (tool) {
        tool.addEventListener('click', function () {
          if (!viewer) return;
          var kind = tool.getAttribute('data-ia-scan-tool');
          var pressed = tool.getAttribute('aria-pressed') === 'true';
          if (kind === 'rotate') {
            viewer.toggleAttribute('auto-rotate', !pressed);
            tool.setAttribute('aria-pressed', String(!pressed));
          } else if (kind === 'clay') {
            setClay(!pressed);
            tool.setAttribute('aria-pressed', String(!pressed));
          } else if (kind === 'light') {
            viewer.setAttribute('environment-image', pressed ? 'neutral' : 'legacy');
            viewer.setAttribute('exposure', pressed ? '1' : '1.25');
            tool.setAttribute('aria-pressed', String(!pressed));
          } else if (kind === 'reset') {
            viewer.cameraOrbit = 'auto auto auto';
            viewer.fieldOfView = 'auto';
            viewer.cameraTarget = 'auto auto auto';
            viewer.jumpCameraToGoal();
          } else if (kind === 'fullscreen') {
            var el = wrap.querySelector('.ia-scan__stage');
            if (document.fullscreenElement) document.exitFullscreen();
            else if (el.requestFullscreen) el.requestFullscreen();
          } else if (kind === 'ar') {
            viewer.activateAR();
          }
        });
      });

      // Block the context menu on the canvas; it is not security, it just
      // stops "save image as" on the render.
      wrap.addEventListener('contextmenu', function (e) {
        if (e.target.closest('model-viewer')) e.preventDefault();
      });
    });
  }

  /* ---------- 360 spin from images ----------
     Frames are plain JPEGs; no geometry is ever downloaded. Dragging the
     full width of the stage turns the car once. */
  // 360 spin inside the product photo gallery (snippets/ia-gallery-spin).
  // It lies over the main photo; the 360 / Photos switch and the 360
  // thumbnail tile show it, and any photo thumbnail, the Photos button or a
  // variant with its own photo hides it again. The spin itself is set up by
  // initSpin like any other.
  function initGallerySpin(root) {
    (root || document).querySelectorAll('[data-ia-gspin]').forEach(function (panel) {
      if (panel.dataset.iaReady) return;
      panel.dataset.iaReady = '1';
      var gallery = panel.closest('[data-product-images]') || panel.parentNode;
      var buttons = gallery.querySelectorAll('[data-ia-gspin-show]');

      function set(open) {
        panel.hidden = !open;
        gallery.classList.toggle('ia-gspin-open', open);
        buttons.forEach(function (b) {
          b.setAttribute('aria-pressed', String((b.dataset.iaGspinShow === 'spin') === open));
        });
        if (!open) {
          var spin = panel.querySelector('[data-ia-spin]');
          if (spin) spin.dispatchEvent(new CustomEvent('ia:spin-stop'));
        }
      }

      buttons.forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          set(b.dataset.iaGspinShow === 'spin');
        });
      });
      // theme.js still moves the slideshow; this only uncovers it.
      gallery.querySelectorAll('.product__thumb-item a').forEach(function (a) {
        a.addEventListener('click', function () { set(false); });
      });
      document.addEventListener('variant:change', function (e) {
        var v = e.detail && e.detail.variant;
        if (v && v.featured_media && !panel.hidden) set(false);
      });
      set(panel.dataset.start !== 'photos');
    });
  }

  function initSpin(root) {
    (root || document).querySelectorAll('[data-ia-spin]').forEach(function (wrap) {
      if (wrap.dataset.iaReady) return;
      wrap.dataset.iaReady = '1';

      var stage = wrap.querySelector('.ia-spin__stage');
      var img = wrap.querySelector('.ia-spin__frame');
      var sheetEl = wrap.querySelector('.ia-spin__sheet');
      var hint = wrap.querySelector('.ia-spin__hint');
      var playBtn = wrap.querySelector('[data-ia-spin-tool="play"]');
      var fullBtn = wrap.querySelector('[data-ia-spin-tool="fullscreen"]');
      var sheet = wrap.dataset.sheet;
      var cols = parseInt(wrap.dataset.cols, 10) || 1;
      var rows = parseInt(wrap.dataset.rows, 10) || 1;
      var frames = [];
      var n;
      if (sheet && sheetEl) {
        n = parseInt(wrap.dataset.count, 10) || cols * rows;
      } else {
        var data = wrap.querySelector('[data-ia-spin-frames]');
        try { frames = JSON.parse(data.textContent); } catch (e) { return; }
        n = frames.length;
      }
      if (!n || n < 2) return;

      var index = 0;
      var cache = [];
      var loaded = false;
      var timer = null;

      // One grid image (one request) or a list of frame files.
      function preload() {
        if (loaded) return;
        loaded = true;
        if (sheet && sheetEl) {
          sheetEl.style.backgroundImage = 'url("' + sheet.replace(/"/g, '%22') + '")';
          return;
        }
        frames.forEach(function (src, i) {
          var im = new Image();
          im.decoding = 'async';
          im.src = src;
          cache[i] = im;
        });
      }

      function show(i) {
        index = ((i % n) + n) % n;
        if (sheet && sheetEl) {
          var col = index % cols;
          var row = Math.floor(index / cols);
          sheetEl.style.backgroundPosition =
            (cols > 1 ? (col / (cols - 1)) * 100 : 0) + '% ' + (rows > 1 ? (row / (rows - 1)) * 100 : 0) + '%';
        } else {
          var cached = cache[index];
          img.src = cached && cached.complete ? cached.src : frames[index];
        }
        stage.setAttribute('aria-valuenow', String(index + 1));
        stage.setAttribute('aria-valuetext', Math.round((index / n) * 360) + '°');
      }

      function stop() {
        if (timer) { clearInterval(timer); timer = null; }
        if (playBtn) playBtn.setAttribute('aria-pressed', 'false');
      }

      function play(once) {
        stop();
        preload();
        var steps = 0;
        if (playBtn) playBtn.setAttribute('aria-pressed', 'true');
        timer = setInterval(function () {
          show(index + 1);
          steps++;
          if (once && steps >= n) stop();
        }, 1000 / 18);
      }

      function hideHint() { if (hint) hint.classList.add('is-hidden'); }

      // Drag / swipe.
      var startX = null;
      var startIndex = 0;
      stage.addEventListener('pointerdown', function (e) {
        stop();
        preload();
        hideHint();
        startX = e.clientX;
        startIndex = index;
        stage.setPointerCapture(e.pointerId);
        stage.classList.add('is-dragging');
      });
      stage.addEventListener('pointermove', function (e) {
        if (startX === null) return;
        var dx = e.clientX - startX;
        show(startIndex - Math.round((dx / stage.clientWidth) * n));
      });
      function endDrag() { startX = null; stage.classList.remove('is-dragging'); }
      stage.addEventListener('pointerup', endDrag);
      stage.addEventListener('pointercancel', endDrag);

      // Keyboard (role=slider).
      stage.addEventListener('keydown', function (e) {
        var step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (e.key === 'Home') { stop(); show(0); e.preventDefault(); return; }
        if (!step) return;
        e.preventDefault();
        stop();
        preload();
        hideHint();
        show(index + step);
      });

      // The gallery switch pauses the spin when it hides it.
      wrap.addEventListener('ia:spin-stop', stop);

      // Top / Underside: a second sheet with the same frames, rendered from
      // below. Fetched on first use, then swapped in at the same angle.
      var views = { top: sheet, under: wrap.dataset.sheetUnder };
      wrap.querySelectorAll('[data-ia-spin-view]').forEach(function (btn, _, all) {
        btn.addEventListener('click', function () {
          var url = views[btn.dataset.iaSpinView];
          if (!url || url === sheet || !sheetEl) return;
          all.forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
          hideHint();
          preload();
          stage.classList.add('is-loading');
          var im = new Image();
          im.onload = im.onerror = function () {
            stage.classList.remove('is-loading');
            if (btn.getAttribute('aria-pressed') !== 'true') return;
            sheet = url;
            sheetEl.style.backgroundImage = 'url("' + url.replace(/"/g, '%22') + '")';
          };
          im.src = url;
        });
      });

      if (playBtn) playBtn.addEventListener('click', function () {
        if (timer) stop(); else play(false);
      });
      if (fullBtn) fullBtn.addEventListener('click', function () {
        if (document.fullscreenElement) document.exitFullscreen();
        else if (stage.requestFullscreen) stage.requestFullscreen();
      });

      // Load frames when the viewer comes into view and spin once to show it
      // turns, unless the visitor prefers reduced motion.
      show(0);
      if ('IntersectionObserver' in window) {
        // Fetch a little before the viewer scrolls in; spin once when it is
        // properly on screen, unless the visitor prefers reduced motion.
        var near = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          near.disconnect();
          preload();
        }, { rootMargin: '300px 0px' });
        near.observe(stage);
        var seen = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          seen.disconnect();
          if (!reduceMotion) setTimeout(function () { if (startX === null) play(true); }, 900);
        }, { threshold: 0.4 });
        seen.observe(stage);
      } else {
        preload();
      }

      wrap.addEventListener('contextmenu', function (e) {
        if (e.target === img || e.target === sheetEl || e.target === stage) e.preventDefault();
      });
    });
  }

  function initAll(root) {
    initReveal(root);
    initRails(root);
    initVideo(root);
    initScan(root);
    initSpin(root);
    initGallerySpin(root);
  }

  ready(function () {
    initAll(document);
    initHeroVideo();
    initStickyAtc();
  });

  // Theme editor: re-run on section load, and bring a selected rail card
  // into view so merchants can see what they are editing.
  // Quick view: theme.js copies the product page's blocks into the pop-up,
  // but scripts copied that way never run. Re-run the description fold
  // (ia-desc-fold) and start the 360 viewer that sits in the pop-up gallery.
  document.addEventListener('quickview:loaded', function () {
    document.querySelectorAll('.modal--quick-shop').forEach(function (modal) {
      modal.querySelectorAll('[data-ia-desc]:not([data-ia-ready]) + script').forEach(function (old) {
        var s = document.createElement('script');
        s.text = old.text;
        old.replaceWith(s);
      });
      initAll(modal);
    });
  });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    // Description dropdowns are built by an inline script (ia-desc-fold),
    // which the editor may not re-run when it swaps a section in.
    if (window.iaFoldDesc) e.target.querySelectorAll('[data-ia-desc]:not([data-ia-ready])').forEach(window.iaFoldDesc);
  });
  document.addEventListener('shopify:block:select', function (e) {
    var card = e.target;
    var track = card && card.closest && card.closest('.ia-rail__track');
    if (track) track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    if (card && card.classList && card.classList.contains('ia-reveal')) card.classList.add('is-visible');
  });
})();
